import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Form, Loader } from "semantic-ui-react";
import { getProxyUsers, patchUser } from "../api/GlobalApi";
import { getMockProxyUsers, patchMockUser } from "../api/mock/MockGlobalApi";
import { handleErrorResponse, handleInputType } from "../Helpers";
import { GlobalConstants } from "./constants/GlobalConstants";
import BCDashboard from "./ui/BCDashboard";
import BCFormInput from "./ui/BCFormInput";
import BCMessage from "./ui/BCMessage";
import "../styles/main.scss";
import { getProfile } from "../api/BrokerApi";
import { getMockProfile } from "../api/mock/MockBrokerApi";
import { IProxyUser } from "./interfaces/Global";
import { FeatureFlags } from "./constants/LocalServerConstants";
import { handleApiPromise } from "../helpers/GlobalHelper";

const BCProxy = ({ 
  isGreaterThanMobile, 
  isGreaterThanTablet, 
  isSidebar, 
  isMessage, 
  setIsMessage, 
  errorResponse, 
  setErrorResponse, 
  user,
  proxyUser, 
  handleProxyUser 
}) => {
  const [isLoading, setIsLoading] = useState(true as boolean);
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [isProxySuccess, setIsProxySuccess] = useState(false as boolean);
  const [isProxyCleared, setIsProxyCleared] = useState(false as boolean);
  const [users, setUsers] = useState([] as IProxyUser[]);
  const [proxySearchValue, setProxySearchValue] = useState("" as string);
  const [isProxyUserSelected, setIsProxyUserSelected] = useState(true as boolean);
  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleRemoveProxy = async () => {
    let isApiSuccess = false;
    if (user?.preferences) {
      const preferences = user?.preferences || {};
      if (preferences?.proxyId) {
        delete preferences.proxyId;
        const userPatchObj = {
          id: user.profileId,
          preferences: JSON.stringify(preferences)
        };
  
        isApiSuccess = await handleApiPromise(
          () => patchUser(userPatchObj),
          () => patchMockUser(userPatchObj),
          (response) => handleErrorResponse(response, setIsMessage, setErrorResponse),
          false
        ) || false;
      }
    }

    if (isApiSuccess) {
      setProxySearchValue("");
      handleProxyUser({});
      setIsProxyCleared(true);
      setTimeout(() => { 
        setIsProxyCleared(false);
      }, 3000);
    }
  };

  const handleProxySearchChange = (value) => {
    setIsProxyUserSelected(false);
    setProxySearchValue(handleInputType("string", value));
  };

  const handleProxySuggestions = () => {
    const results = [] as any[];
    if (users && users.length > 0) {
      const nameSearchValueArray = proxySearchValue.split(" ");
      let nameSearchValue1 = "";
      let nameSearchValue2 = "";
      if (nameSearchValueArray[0]) {
        nameSearchValue1 = nameSearchValueArray[0];
      }

      if (nameSearchValueArray[1]) {
        nameSearchValue2 = nameSearchValueArray[1];
      }

      const filteredUsers = users.filter(x => ((x.email && (x.email).toLowerCase().includes(proxySearchValue.toLowerCase())) ||
        (((x.firstName && nameSearchValue1 !== "") && (x.firstName).toLowerCase().includes(nameSearchValue1.toLowerCase())) &&
        ((x.lastName && nameSearchValue2 !== "") && (x.lastName).toLowerCase().includes(nameSearchValue2.toLowerCase())))));
    
      let resultLimit = 6;
      filteredUsers.forEach(filteredUser => {
        const foundResultIndex = results.findIndex(x => x.description === filteredUser.email);
        if (resultLimit > 0 && foundResultIndex === -1) {
          results.push({
            key: filteredUser.id,
            title: `${filteredUser.firstName} ${filteredUser.lastName}`,
            description: filteredUser.email,
            value: filteredUser.id,
            firstName: filteredUser.firstName,
            lastName: filteredUser.lastName
          });

          resultLimit--;
        }
      });
    }
    
    return results;
  };

  const handleProxySelect = async (result) => { 
    const foundUser = users.find(x => x.id === result.key);
    let isApiSuccess = false;
    if (foundUser && foundUser.id) {
      const proxyProfile = await handleApiPromise(
        () => getProfile(foundUser.id as any),
        () => getMockProfile(),
        (response) => handleErrorResponse(response, setIsMessage, setErrorResponse),
        true,
        {}
      ) || {};

      if (user?.profileId && proxyProfile?.id) {
        const preferences = user?.preferences || {};
        preferences.proxyId = foundUser.id;
        const userPatchObj = {
          id: user.profileId,
          preferences: JSON.stringify(preferences)
        };

        isApiSuccess = await handleApiPromise(
          () => patchUser(userPatchObj),
          () => patchMockUser(userPatchObj),
          (response) => handleErrorResponse(response, setIsMessage, setErrorResponse),
          false
        ) || false;
      }

      if (isApiSuccess) {
        setProxySearchValue(result.title);
        handleProxyUser(proxyProfile);
        setIsProxySuccess(true);
        setTimeout(() => { 
          setIsProxySuccess(false);
        }, 3000);
      }
    }

    if (!isApiSuccess) {
      setProxySearchValue("");
    }
    
    setIsProxyUserSelected(true);
  };

  const handleProxyKeyDown = (e) => {
    if (e.keyCode === 27) {
      setIsProxyUserSelected(true);
      if (proxyUser.id) {
        if (proxyUser.firstName && proxyUser.lastName) {
          setProxySearchValue(`${proxyUser.firstName} ${proxyUser.lastName}`);
        }
        else if (proxyUser.name) {
          setProxySearchValue(proxyUser.name);
        }
      }
      else {
        setProxySearchValue("");
      }
      
      e.target.blur();
    }
  };

  useEffect(() => {
    const getUsersAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockProxyUsers() : await getProxyUsers();
        if (response && response.ok) {
          setIsMessage(false);
          setUsers(await response.json());
          setIsLoading(false);
        }
        else {
          setIsLoading(false);
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        setIsMessage(false);
        setIsLoading(false);
        handleError();
      }
    };

    if (isFirstLoad) {
      getUsersAsync();
      if (proxyUser.id) {
        if (proxyUser.firstName && proxyUser.lastName) {
          setProxySearchValue(`${proxyUser.firstName} ${proxyUser.lastName}`);
        }
        else if (proxyUser.name) {
          setProxySearchValue(proxyUser.name);
        }
      }
    }
    
  }, [setIsMessage, setErrorResponse, handleError, proxyUser, isFirstLoad]);

  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  const ProxyLayout = [
    [
      {
        size: 16,
        content: <React.Fragment>
          <Form>
            <BCFormInput
              tooltip="Search for a user then select the one you wish to proxy for." 
              label={!proxyUser.id && "Select Your Proxy"}
              actionLabel="Select Your Proxy"
              actionText={proxyUser.id ? "Clear Proxy" : ""}
              actionLabelClass="primary-red"
              labelAction={() => handleRemoveProxy()}
              type={GlobalConstants.FormInputTypes.SEARCH_FILTER}
              onSearchChange={(value) => handleProxySearchChange(value)}
              value={proxySearchValue}
              renderSuggestions={handleProxySuggestions()}
              handleSearchSelect={(e, data) => handleProxySelect(data.result)}
              isSearchOpen={!isProxyUserSelected}
              onSearchFocus={() => setIsProxyUserSelected(false)}
              handleSearchKeyDown={handleProxyKeyDown}
              searchClass="bc-search-input-full"
            />
          </Form>
        </React.Fragment>
      }
    ]
  ];

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      {isLoading ? <Loader className="" active inline="centered" size={"big"} /> : <React.Fragment>
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isMessage}
          transitionOnMount
          styles="full-width"
          onDismiss={() => setIsMessage(false)}
          errorResponse={errorResponse}
        />
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isProxySuccess}
          transitionOnMount
          styles="full-width"
          color="green"
          onDismiss={() => setIsProxySuccess(false)}
          header={"Success!"}
          text={`You are now proxied as ${proxySearchValue}.`}
        />
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isProxyCleared}
          transitionOnMount
          styles="full-width"
          color="green"
          onDismiss={() => setIsProxyCleared(false)}
          header={"Success!"}
          text={"You are no longer proxied."}
        />
        <BCDashboard
          DashboardLayout={ProxyLayout}
          isGreaterThanMobile={isGreaterThanMobile}
        />
      </React.Fragment>}
    </div>
  );
};

export default BCProxy;